/**
 * @fileoverview Hook for generating declaration-related alerts
 * Shows persistent alert banner when doctors need to accept institution link declaration
 */
import { useMemo, useContext } from 'react';
import { intl, LanguageBaseContext } from 'dmsbasereact';

/**
 * Custom hook to generate declaration alerts for doctors
 * @param {string} userType - Current user type ('doctor', 'pharmacist', etc.)
 * @param {boolean} hasDeclaration - Whether doctor has accepted ALL declarations
 * @param {boolean} loading - Whether declaration status is still loading
 * @param {Object} declarationStatus - Full declaration status object with institutions
 * @returns {Array} Array of alert objects
 */
export const useDeclarationAlerts = (userType, hasDeclaration, loading, declarationStatus = null) => {
  const { language } = useContext(LanguageBaseContext);

  return useMemo(() => {
    const alerts = [];

    // Only show alert for doctors who haven't accepted the declaration
    // Don't show while loading to avoid flash of alert
    if (userType === 'doctor' && !hasDeclaration && !loading) {
      // Find institutions without declaration
      const institutionsWithoutDeclaration = declarationStatus?.institutions?.filter(
        inst => !inst.doctor_declaration_accepted
      ) || [];

      const institutionNames = institutionsWithoutDeclaration
        .map(inst => inst.institution_name)
        .join(', ');

      const message = institutionNames
        ? `${intl.get('DeclarationRequiredAlert').d(
            'Você precisa aceitar a Declaração de Vínculo Institucional nas configurações do seu perfil para ter acesso completo às funcionalidades de prescrição e treinamento.'
          )} Instituições: ${institutionNames}`
        : intl.get('DeclarationRequiredAlert').d(
            'Você precisa aceitar a Declaração de Vínculo Institucional nas configurações do seu perfil para ter acesso completo às funcionalidades de prescrição e treinamento.'
          );

      alerts.push({
        id: 'declaration-required',
        severity: 'warning',
        title: intl.get('DeclarationRequiredTitle').d('Declaração Obrigatória'),
        message,
        dismissible: false,
        link: {
          label: intl.get('GoToProfileSettings').d('Ir para Configurações de Perfil'),
          to: '/profile-settings?tab=institution' // Opens Institution tab where declaration checkbox is located
        }
      });
    }

    return alerts;
  }, [userType, hasDeclaration, loading, declarationStatus, language]); // eslint-disable-line react-hooks/exhaustive-deps -- language is required for i18n updates
};

export default useDeclarationAlerts;
